import { useParams } from "react-router-dom";
import { Loader } from "semantic-ui-react";

import { useScrollPageToTop } from "hooks";
import { createFormHelper } from "lib/Form";
import { Configuration, DealerDetailsActions, DealerDetailsHeader, DmsSystem, GdprAvg, General, Invoicing, PonOil, Service } from "modules/DealerDetails/components";
import "modules/DealerDetails/DealerDetails.scss";
import { useDealerDetailsQuery, useUpdateDealerMutation } from "modules/DealerDetails/hooks";
import { NotFoundPage } from "router/components";
import { RESOURCE_NOT_FOUND } from "utils/Api";

import type { Dealer } from "models";

export type DealerAdminFormPayload = Pick<
  Dealer,
  | "active"
  | "name"
  | "country_code"
  | "region"
  | "city"
  | "street"
  | "postal_code"
  | "phone_nr"
  | "website_url"
  | "sys_admin_name"
  | "sys_admin_email"
  | "sys_admin_phone"
  | "dms_server_info"
  | "network_info"
  | "can_add_users"
  | "dbb_enabled"
  | "custom_domain_enabled"
  | "enable_car_ready"
  | "advised_critical_history_enabled"
  | "customer_data_retention_in_years"
  | "is_pon_enabled"
  | "pon_username"
  | "lef_username"
  | "is_exact_invoicing_enabled"
> & {
  pon_password?: string;
  lef_password?: string;
};

const FormHelper = createFormHelper<DealerAdminFormPayload>();

const clearDisabledServiceSecrets = (values: DealerAdminFormPayload): DealerAdminFormPayload => ({
  ...values,
  ...(!values.is_pon_enabled && { pon_username: "", pon_password: undefined })
});

const DEALER_ID_REGEX = /^\d+$/;
const parseDealerId = (dealerId: string) => (DEALER_ID_REGEX.test(dealerId) ? Number(dealerId) : undefined);

const isFormInvalid = ({ name, is_pon_enabled, pon_username, pon_password }: DealerAdminFormPayload, hasPonPassword?: boolean) => {
  const isPonValid = !is_pon_enabled || (pon_username && (hasPonPassword || pon_password));

  return !(name && isPonValid);
};

export const DealerDetails = () => {
  const dealerId = parseDealerId(useParams<{ dealerId: string }>().dealerId);

  const { data: dealer, isLoading: isDealerDetailsLoading, error: dealerDetailsError } = useDealerDetailsQuery(dealerId);
  const { mutate: updateDealer, isPending: isUpdateDealerPending } = useUpdateDealerMutation();
  useScrollPageToTop();

  if (!dealerId) return <NotFoundPage />;
  if (isDealerDetailsLoading) return <Loader active />;
  if (dealerDetailsError?.message.includes(RESOURCE_NOT_FOUND)) return <NotFoundPage />;
  if (!dealer) return null;

  const onSubmit = (values: DealerAdminFormPayload) => {
    if (isFormInvalid(values, dealer.has_pon_password)) return;

    const { pon_password, ...dealerValues } = clearDisabledServiceSecrets(values);
    const payload = { ...dealer, ...dealerValues, pon_oil_password: pon_password };

    updateDealer(payload);
  };

  return (
    <FormHelper.Form onSubmit={onSubmit} defaultValues={{ ...dealer }}>
      <div className="DealerDetails">
        <DealerDetailsHeader isUpdateDealerPending={isUpdateDealerPending} />
        <General tabId="general" />
        <GdprAvg tabId="gdprAvg" />
        <Invoicing tabId="invoicing" dealer={dealer} />
        <DmsSystem tabId="dmsSystem" />
        <Configuration tabId="configuration" />
        <PonOil tabId="ponOil" dealer={dealer} />
        <Service tabId="service" dealer={dealer} />
        <DealerDetailsActions dealerId={dealer.id} dealerName={dealer.name} />
      </div>
    </FormHelper.Form>
  );
};
